import axios from 'axios';
import { authFetch, createApiClient } from './httpClient';

const API_BASE_URL = process.env.REACT_APP_TEMPLATE_BASE_URL;
const API_URL = process.env.REACT_APP_REST_BASE_URL;

// Create axios instance with base URL
const api = createApiClient({
  baseURL: API_BASE_URL,
});

//
// ============================
// Template API calls
// ============================
//

const getErrorMessage = (error, fallback) => {
  // Try to extract message from various error response formats
  let serverMessage = null;
  
  // Check axios error response
  if (error?.response?.data) {
    serverMessage = error.response.data.detail || error.response.data.message;
  }
  
  // Fall back to error message
  if (!serverMessage) {
    serverMessage = error?.message;
  }
  
  // Return message if it's a non-empty string
  if (typeof serverMessage === 'string' && serverMessage.trim()) {
    return serverMessage;
  }
  
  return fallback;
};

export const getTemplates = async (fromDate = null, toDate = null, sortOrder = 'desc', selectedUserId = null, skipDefaultDateFilter = false) => {
  const params = new URLSearchParams();
  if (fromDate) params.append('fromDate', fromDate);
  if (toDate) params.append('toDate', toDate);
  if (sortOrder) params.append('sortOrder', sortOrder);
  if (selectedUserId) params.append('selectedUserId', selectedUserId);
  if (skipDefaultDateFilter) params.append('skip_default_date_filter', 'true');

  const queryString = params.toString();
  const url = queryString ? `/templates?${queryString}` : '/templates';
  try {
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to load job openings'));
  }
};

export const getTemplate = async (id) => {
  try {
    const response = await api.get(`/templates/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to load job opening'));
  }
};

export const createTemplate = async (templateData) => {
  try {
    const response = await api.post('/templates', templateData);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to create job opening'));
  }
};

export const updateTemplate = async (id, templateData) => {
  try {
    const response = await api.put(`/templates/${id}`, templateData);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update job opening'));
  }
};

export const generateQuestionBank = async (id) => {
  try {
    const response = await api.post(`/question_bank/templates/${id}/generate-question-bank`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to generate AI question bank'));
  }
};

export const deleteTemplate = async (id) => {
  try {
    const response = await api.delete(`/templates/${id}`);
    return response.data;
  } catch (error) {
    console.error("Raw axios error:", error);
    console.error("Error response data:", error?.response?.data);
    console.error("Error message:", error?.message);
    const message = getErrorMessage(error, 'Failed to delete job opening');
    console.error("Extracted message:", message);
    throw new Error(message);
  }
};

export const assignTemplate = async (templateId, assignedTo) => {
  try {
    const response = await api.put(`/templates/${templateId}/assign`, { assigned_to: assignedTo });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to assign job opening'));
  }
};

//
// ============================
// Interview API calls
// ============================
//

export const getInterviews = async (
  fromDate = null,
  toDate = null,
  sortOrder = 'desc',
  templateId = null,
  skipDefaultDateFilter = false,
  selectedUserId = null,
) => {
  const params = new URLSearchParams();
  if (fromDate) params.append('fromDate', fromDate);
  if (toDate) params.append('toDate', toDate);
  if (sortOrder) params.append('sortOrder', sortOrder);
  if (templateId) params.append('templateId', templateId);
  if (selectedUserId) params.append('selectedUserId', selectedUserId);
  if (skipDefaultDateFilter) params.append('skip_default_date_filter', 'true');

  const queryString = params.toString();
  const url = queryString ? `/interviews?${queryString}` : '/interviews';
  const response = await api.get(url);
  return response.data;
};

export const getInterview = async (id) => {
  const response = await api.get(`/interviews/${id}`);
  return response.data;
};

export const scheduleInterview = async (formData) => {
  try {
    const response = await api.post('/interviews', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to schedule interview'));
  }
};

export const updateInterview = async (id, formData) => {
  try {
    const response = await api.put(`/interviews/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update interview'));
  }
};

export const deleteInterview = async (id) => {
  try {
    const response = await api.delete(`/interviews/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to delete interview'));
  }
};

export const calculateATSScore = async (interviewId, jd, resumeFile) => {
  try {
    const formData = new FormData();
    formData.append('interviewId', interviewId);
    formData.append('jd', jd);
    formData.append('resume', resumeFile);

    const response = await api.post('/interviews/ats-score', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to calculate ATS score'));
  }
};

export const downloadResume = async (id) => {
  try {
    const response = await api.get(`/interviews/${id}/resume`, {
      responseType: 'blob',
    });
    return response;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to download resume'));
  }
};

export const sendInterviewInvite = async (payload) => {
  try {
    const response = await api.post('/interviews/generate-interview-link', payload);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to send interview invite'));
  }
};

export const generateTemplateLink = async (templateId) => {
  const response = await authFetch(`${API_URL}/job_template_link/${templateId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'accept': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to generate template link');
  }

  return response.json();
};


export const uploadCandidatePhoto = async (sessionId) => {
  const formData = new FormData();
  
  formData.append("interview_id",sessionId);

  const response = await authFetch(`${API_URL}/video_proctoring/upload-candidate-photo`, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to upload candidate photo");
  }

  return await response.json();
};

export const getRecordingUrl = async (interviewId) => {
  const response = await api.get(`/storage/get-video/${interviewId}`);
  return response.data; // { videoUrl: "https://..." }
};

export const getInterviewReport = async (interviewId) => {
  const response = await api.get(`/storage/get-pdf/${interviewId}`, {
    responseType: "blob", // PDF will come as file
  });
  return response.data; // blob (pdf file)
};

export const getProctoringAnalysis = async (interviewId) => {
  const response = await authFetch(`${API_URL}/video_proctoring/proctoring-analysis/${interviewId}`, {
    method: "GET",
    headers: {
      "accept": "application/json",
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to fetch proctoring analysis");
  }

  return response.json();
};

export const getSummaryData = async (interviewId) => {
  const response = await api.get(`/storage/summary-data/${interviewId}`);
  return response.data;
};

export const getTranscriptData = async (interviewId) => {
  const response = await api.get(`/storage/transcript-data/${interviewId}`);
  return response.data;
};

export const downloadSummaryPDF = async (interviewId) => {
  const response = await api.get(`/storage/summary-pdf/${interviewId}`, {
    responseType: "blob",
  });
  return response.data;
};

export const downloadTranscriptPDF = async (interviewId) => {
  const response = await api.get(`/storage/transcript-pdf/${interviewId}`, {
    responseType: "blob",
  });
  return response.data;
};

//
// ============================
// Code Evaluation API calls
// ============================
//

export const getCodeEvaluationLanguages = async () => {
  try {
    const response = await authFetch(`${API_URL}/code_evaluation/languages`, {
      method: 'GET',
      headers: {
        'accept': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch languages');
    }

    const data = await response.json();
    return data.languages || [];
  } catch (error) {
    console.error('Error fetching code evaluation languages:', error);
    return [];
  }
};

export const uploadJobDescription = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await authFetch(
      `${API_URL}/skills_extractor/job_description_upload`,
      {
        method: 'POST',
        body: formData,
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to upload job description');
    }

    return await response.json();
  } catch (error) {
    console.error('Error uploading job description:', error);
    throw error;
  }
};

//
// ============================
// Contact Form API calls
// ============================
//

// Add this function to your existing api.js file

export const generateCaptcha = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/contact/generate-captcha`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to generate captcha');
    }

    return await response.json();
  } catch (error) {
    console.error('Error generating captcha:', error);
    throw error;
  }
};

export const submitContactForm = async (contactData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/contact/contact-submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(contactData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to submit form');
    }

    return await response.json();
  } catch (error) {
    console.error('Error submitting contact form:', error);
    throw error;
  }
};

export const getJobDivaJobDetail = async (jobdivaref) => {
  try {
    const response = await authFetch(
      `${API_URL}/jobdiva/job/${encodeURIComponent(jobdivaref)}`,
      {
        method: "GET",
        headers: {
          accept: "application/json",
        },
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error.detail || "Failed to fetch JobDiva job details"
      );
    }

    return await response.json();
  } catch (error) {
    console.error(
      `Error fetching JobDiva job (${jobdivaref}):`,
      error
    );
    throw error;
  }
};

export const scrapeJobFromUrl = async (url) => {
  try {
    const response = await authFetch(
      `${API_URL}/extract/scrape_job?url=${encodeURIComponent(url)}`,
      {
        method: "POST",
        headers: {
          accept: "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch job details");
    }

    return await response.json();
  } catch (error) {
    console.error("Error scraping job:", error);
    throw error;
  }
};

//
// ============================
// Coding Questions API calls
// ============================
//

export const getCodingQuestions = async (language = null, complexity = null) => {
  try {
    const params = new URLSearchParams();
    if (language) params.append('language', language);
    if (complexity) params.append('complexity', complexity);
    const queryString = params.toString();
    const url = queryString 
      ? `${API_URL}/coding-questions/get-questions?${queryString}` 
      : `${API_URL}/coding-questions/get-questions`;
      
    const response = await authFetch(url, {
      method: 'GET',
      headers: {
        'accept': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch coding questions');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching coding questions:', error);
    throw error;
  }
};

export const createCodingQuestion = async (questionData) => {
  try {
    const response = await authFetch(`${API_URL}/coding-questions/create-question`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(questionData),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to create coding question');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating coding question:', error);
    throw error;
  }
};

export const updateCodingQuestion = async (questionId, questionData) => {
  try {
    const response = await authFetch(`${API_URL}/coding-questions/update-questions/${questionId}`, {
      method: 'PUT',
      headers: {
        'accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(questionData),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to update coding question');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating coding question:', error);
    throw error;
  }
};

export const deleteCodingQuestion = async (questionId) => {
  try {
    const response = await authFetch(`${API_URL}/coding-questions/delete-questions/${questionId}`, {
      method: 'DELETE',
      headers: {
        'accept': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to delete coding question');
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting coding question:', error);
    throw error;
  }
};

// Export axios instance for custom use if needed
export default api;
